import { apiClient } from '@/core/api/client';
import { Endpoints } from '@/core/api/endpoints';
import type {
    AdhaUaePassLoginRequest,
    AdhaUaePassLoginResponse,
    AuthClientPlatform,
} from '@/core/api/models/auth';
import { getAdhaBasicAuthHeaders } from '@/core/constants/adhaJourney';
import { logger } from '@/core/logger';
import { deviceIdService } from '@/core/services/deviceIdService';
import { getClientPlatform } from '@/core/utils/clientPlatform';
import { encryptRsaOaep } from '@/core/wrappers/rsaOaep';
import type { AuthResponse, LoginCredentials } from '../types';

/** Shared by send-OTP and verify-OTP so the process name cannot drift. */
export const ADHA_LOGIN_VERIFICATION_PROCESS_TYPE = 'ADHA Login Verification';

function getAuthClientPlatform(): AuthClientPlatform {
    return getClientPlatform();
}

class AuthService {
    async login(credentials: LoginCredentials): Promise<AuthResponse> {
        // Get device ID if not provided
        // deviceIdService always returns a string (even if it's a fallback)
        let deviceId = credentials.device_id;
        if (!deviceId) {
            try {
                deviceId = await deviceIdService.getDeviceId();
                logger.info('AuthService', 'Device ID retrieved for login', { deviceId });
            } catch (error) {
                logger.error('AuthService', 'Error getting device ID for login', error);
                // deviceIdService has its own fallback, but if it still fails, use a basic fallback
                deviceId = `fallback_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
                logger.warn('AuthService', 'Using fallback device ID', { deviceId });
            }
        }

        // Always include device_id in the request payload
        const payload: LoginCredentials = {
            ...credentials,
            device_id: deviceId,
        };

        logger.info('AuthService', 'Sending login request with device_id', { 
            email: credentials.email,
            hasDeviceId: !!payload.device_id,
            deviceId: payload.device_id 
        });

        return await apiClient.post<AuthResponse, LoginCredentials>(
            Endpoints.auth.manualLogin,
            payload
        );
    }

    /**
     * ADHA flow only. Existing UAE Pass callback login (handleCallback) is unaffected.
     * Uses Basic Auth from EXPO_PUBLIC_ADHA_BASIC_USER / EXPO_PUBLIC_ADHA_BASIC_PASSWORD;
     * apiClient will not overwrite with Bearer.
     */
    async loginWithAdhaUaePassCode(oauthCode?: string): Promise<AdhaUaePassLoginResponse> {
        const platform = getAuthClientPlatform();
        const code = oauthCode?.trim();
        if (!code) {
            throw new Error('ADHA UAE Pass login requires an OAuth code');
        }
        const payload: AdhaUaePassLoginRequest = {
            code,
            platform,
        };

        logger.info('AuthService', 'Sending ADHA UAE Pass login request (Basic Auth)', {
            platform,
            hasOAuthCode: Boolean(oauthCode?.trim()),
        });

        return await apiClient.post<AdhaUaePassLoginResponse, AdhaUaePassLoginRequest>(
            Endpoints.auth.uaePassLogin,
            payload,
            {
                headers: getAdhaBasicAuthHeaders(),
            },
        );
    }
    /**
     * Emirates ID flow: Step 1 - Sign in with Emirates ID.
     * The ID is RSA-OAEP encrypted before it leaves the device.
     */
    async loginWithEmiratesId(emiratesId: string): Promise<{
        status: string;
        message: string;
        data: {
            jwtToken: string;
            email: string;
            phoneNumber: string;
        };
    }> {
        const encryptedEmiratesId = await encryptRsaOaep(emiratesId);
        logger.info('AuthService', 'Sending Emirates ID sign-in request (Basic Auth)');
        return await apiClient.post(
            Endpoints.auth.signIn,
            { emiratesId: encryptedEmiratesId },
            {
                headers: getAdhaBasicAuthHeaders(),
            }
        );
    }

    /**
     * Emirates ID flow: Step 2 - Send OTP
     */
    async sendEmiratesIdOtp(
        tempToken: string,
        commType: 'Email' | 'SMS',
        contactInfo: { mobileNumber?: string; email?: string }
    ): Promise<{
        status: string;
        message: string;
        data?: { transactionId?: string; jwtToken?: string };
        details?: { jwtToken?: string; message?: string };
        jwtToken?: string;
    }> {
        logger.info('AuthService', 'Sending Emirates ID OTP request', { commType });
        return await apiClient.post(
            Endpoints.registration.sendOtp, // reusing sendOtp endpoint URL
            {
                processType: ADHA_LOGIN_VERIFICATION_PROCESS_TYPE,
                commType,
                ...contactInfo,
            },
            {
                headers: {
                    Authorization: `Bearer ${tempToken}`,
                },
            }
        );
    }

    /**
     * Emirates ID flow: Step 3 - Verify OTP.
     * `otpToken` is the jwtToken returned by send-OTP. It is sent only as
     * Authorization, the same way other authenticated calls send a bearer token.
     * The OTP is encrypted only when send-OTP used ADHA_LOGIN_VERIFICATION_PROCESS_TYPE.
     */
    async verifyEmiratesIdOtp(
        otpToken: string,
        otp: string,
        processType: string
    ): Promise<AuthResponse> {
        const shouldEncryptOtp = processType === ADHA_LOGIN_VERIFICATION_PROCESS_TYPE;
        const otpValue = shouldEncryptOtp ? await encryptRsaOaep(otp) : otp;
        logger.info('AuthService', 'Sending Emirates ID OTP verification request', {
            encryptOtp: shouldEncryptOtp,
        });
        return await apiClient.post(
            Endpoints.registration.verifyOtp, // reusing verifyOtp endpoint URL
            {
                otp: otpValue,
            },
            {
                headers: {
                    Authorization: `Bearer ${otpToken}`,
                },
            }
        );
    }
}

/** jwtToken from POST /v1/customers/send-otp, used as the verify-OTP bearer. */
export function readSendOtpJwtToken(response: {
    data?: { jwtToken?: string };
    details?: { jwtToken?: string };
    jwtToken?: string;
}): string {
    const token =
        response.data?.jwtToken ||
        response.details?.jwtToken ||
        response.jwtToken ||
        '';
    return token.trim();
}

export const authService = new AuthService();
